import { useTranslation } from "react-i18next";
import { cn } from "@nexus/ui";
import { costDisplay } from "../lib/cost-display";
import { formatTokensExact } from "../lib/format-tokens";

interface CostValueProps {
  costMicros: number;
  /**
   * Tokens the cost does not cover. A model row has no partial case — it is
   * either priced or not — so it passes its whole total when it is unpriced.
   */
  unpricedTokens: number;
}

/**
 * A row's cost, shared by the model, feature and tenant tables so the three
 * cannot drift on how they report an incomplete one. A partial figure carries a
 * `+`: the alternative is a bare number that reads as the whole cost of the row
 * when some of its tokens have no rate at all.
 */
export function CostValue({ costMicros, unpricedTokens }: CostValueProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const display = costDisplay({ costMicros, unpricedTokens }, locale);

  const title = display.isUnpriced
    ? t(
        "admin.llmUsage.table.costUnpricedTitle",
        "Nothing here has a price set, so there is no cost to show",
      )
    : display.isPartial
      ? t(
          "admin.llmUsage.table.costPartialTitle",
          "At least this much — {{tokens}} tokens have no price set",
          { tokens: formatTokensExact(unpricedTokens, locale) },
        )
      : undefined;

  return (
    <span className={cn(display.isUnpriced && "text-muted-foreground")} title={title}>
      {display.text}
      {display.isPartial ? <span className="text-muted-foreground">+</span> : null}
    </span>
  );
}
